/**
 * ExpertRoadmapsSection.tsx
 * Displays expert-made roadmaps in a separate section of the dashboard
 * Shows below user-created roadmaps with "Expert Made" badge
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Trophy,
  BookOpen,
  ArrowRight,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { getAdminRoadmaps } from '@/services/adminRoadmapService';
import type { AdminRoadmapWithDays } from '@/services/adminRoadmapService';

interface ExpertRoadmapsSectionProps {
  isDark: boolean;
  onSelectRoadmap: (roadmap: AdminRoadmapWithDays) => void;
  userEmail?: string | null;
}

export function ExpertRoadmapsSection({
  isDark,
  onSelectRoadmap,
  userEmail,
}: ExpertRoadmapsSectionProps) {
  const [roadmaps, setRoadmaps] = useState<AdminRoadmapWithDays[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExpertRoadmaps();
  }, []);

  const loadExpertRoadmaps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Get published roadmaps only
      const { data, error: err } = await getAdminRoadmaps(false);
      if (err) throw err;
      setRoadmaps(data);
    } catch (err) {
      console.error('Error loading expert roadmaps:', err);
      setError('Failed to load expert roadmaps');
    } finally {
      setLoading(false);
    }
  }, []);

  const bgColor = isDark ? 'bg-zinc-900/50' : 'bg-white/50';
  const cardBg = isDark ? 'bg-zinc-900' : 'bg-white';
  const borderColor = isDark ? 'border-zinc-800' : 'border-zinc-200';
  const textColor = isDark ? 'text-white' : 'text-zinc-900';
  const subTextColor = isDark ? 'text-zinc-400' : 'text-zinc-600';

  if (loading) {
    return (
      <div className={`rounded-2xl border ${borderColor} ${bgColor} p-6 md:p-8 backdrop-blur-sm`}>
        <div className="flex items-center justify-center py-12">
          <Loader className={`w-8 h-8 animate-spin ${subTextColor}`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-2xl border ${borderColor} ${bgColor} p-6 md:p-8 backdrop-blur-sm`}>
        <div className="flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (roadmaps.length === 0) {
    return null; // Don't show section if no expert roadmaps
  }

  return (
    <div className={`rounded-2xl border ${borderColor} ${bgColor} p-6 md:p-8 backdrop-blur-sm space-y-6 animate-in slide-in-from-bottom-4`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
          <Trophy className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className={`text-2xl font-bold ${textColor}`}>Premium Roadmaps</h2>
          <p className={`text-sm ${subTextColor}`}>
            Curated learning paths designed by experts
          </p>
        </div>
      </div>

      {/* Roadmaps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roadmaps.map((roadmap) => (
          <button
            key={roadmap.id}
            onClick={() => onSelectRoadmap(roadmap)}
            className={`group relative min-h-[240px] md:min-h-[280px] rounded-3xl p-6 cursor-pointer transition-all card-lift overflow-hidden ${
              isDark 
                ? 'bg-white/[0.03] border border-white/5 hover:bg-white/[0.06]' 
                : 'bg-white border border-zinc-200/80 hover:border-zinc-300 shadow-sm hover:shadow-xl'
            }`}
          >
            {/* Gradient accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-rose-500" />
            
            {/* Content */}
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 ${
                      isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                    }`}>
                      <Trophy className="w-3 h-3" />
                      Premium
                    </span>
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold ${
                      isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-100 text-zinc-500'
                    }`}>
                      {roadmap.difficulty || 'Beginner'}
                    </span>
                  </div>
                  <h3 className={`text-lg font-bold leading-snug line-clamp-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                    {roadmap.title}
                  </h3>
                </div>
              </div>

              {/* Description */}
              {roadmap.description && (
                <p className={`text-sm line-clamp-2 mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  {roadmap.description}
                </p>
              )}

              {/* Meta */}
              <div className={`flex items-center gap-4 text-sm mb-auto ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                <div className="flex items-center gap-1.5">
                  <BookOpen className="w-4 h-4" />
                  <span>{roadmap.total_days} days</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-100 text-zinc-600'
                  }`}>
                    {roadmap.roadmap_type}
                  </span>
                </div>
              </div>

              {/* Divider */}
              <div className={`my-4 border-t ${isDark ? 'border-white/10' : 'border-zinc-200'}`} />

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center">
                  <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                    {Math.round((roadmap.days?.length || 0) * 0.6)}h
                  </div>
                  <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Hours</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                    {roadmap.days?.length || 0}
                  </div>
                  <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Lessons</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold text-yellow-500`}>
                    ★
                  </div>
                  <div className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Premium</div>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* View All Button */}
      {roadmaps.length > 6 && (
        <div className="text-center pt-4">
          <button className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 hover:bg-red-500/20 transition font-medium text-sm">
            View All Premium Roadmaps
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
